import React, { useState, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';

const CustomAutocomplete = ({ options, value, onChange, placeholder, getDropdownOptions, dropdownId, openDropdownId, setOpenDropdownId, disabled = false }) => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Handle outside click to close the dropdown
  useEffect(() => {
    const handleOutsideClick = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && openDropdownId === dropdownId) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openDropdownId, dropdownId, setOpenDropdownId]);

  const debouncedFetchOptions = debounce(input => {
    if (!disabled) {
      getDropdownOptions(input);
    } // Only fetch if not disabled
  }, 300);

  // const handleSearchInputChange = e => {
  //   if (disabled) {
  //     return;
  //   } // Prevent input changes when disabled
  //   const input = e.target.value;
  //   setSearchInputValue(input);
  //   if (input) {
  //     debouncedFetchOptions(input);
  //   } else {
  //     setFilteredOptions(options);
  //   }
  // };

  const handleSearchInputChange = e => {
    if (disabled) return;

    const input = e.target.value;
    setSearchInputValue(input);

    if (input.trim()) {
      debouncedFetchOptions(input);
    } else {
      debouncedFetchOptions.cancel(); // Cancel any pending debounced calls
      // Use the value prop as the search term when input is empty
      if (value) {
        debouncedFetchOptions(value);
      } else {
        setFilteredOptions(options); // Fallback to all options if no value
      }
    }
  };



  const handleOptionClick = option => {
    if (disabled) {
      return;
    } // Prevent option selection when disabled
    onChange(option);
    setSearchInputValue('');
    setOpenDropdownId(null);
  };

  const toggleDropdown = () => {
    if (disabled) {
      return;
    } // Prevent dropdown toggle when disabled
    setOpenDropdownId(openDropdownId === dropdownId ? null : dropdownId);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Main Input */}
      <div className={`hover:border-actions-primary relative flex w-full items-center rounded-lg border-2 border-[#D8E0F0] bg-white p-1 px-3 ${disabled ? 'cursor-not-allowed bg-gray-200 opacity-50' : 'cursor-pointer'}`} onClick={toggleDropdown}>
        <div className="h-6 flex-grow overflow-hidden text-ellipsis text-[#7D8592]">
          {value || placeholder}
        </div>
        {/* Dropdown Arrow */}
        <hr />
        <div className="ml-2 flex-shrink-0">
          {openDropdownId === dropdownId ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#7D8592]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 15l6-6 6 6" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#7D8592]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          )}
        </div>
      </div>

      {/* Dropdown List */}
      {openDropdownId === dropdownId && !disabled && (
        <div className="hover:border-actions-primary absolute z-10 mt-1 w-full overflow-auto rounded-lg border-2 border-[#D8E0F0] bg-white text-[#7D8592]">
          {/* Search Input in Dropdown */}
          <div className="relative flex items-center border-b-2 border-[#D8E0F0] p-2 text-[#7D8592]">
            <input type="text" value={searchInputValue} onChange={handleSearchInputChange} placeholder="Search..." disabled={disabled} // Disable the input element
              className={`w-full rounded border-2 border-[#D8E0F0] bg-white p-2 pl-8 text-[#7D8592] placeholder-gray-800 focus:outline-none ${disabled ? 'cursor-not-allowed bg-gray-200' : ''}`}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-[#515050]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </div>

          {/* Filtered Options */}
          <ul className="max-h-40 overflow-auto">
            {filteredOptions.map((option, index) => (
              <li key={index} onClick={() => handleOptionClick(option)} className={`cursor-pointer p-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#D8E0F0] hover:text-black'}`}>{option}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomAutocomplete;
